import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import AuthGuard from '../guards/AuthGuard';
import ClientGuard from '../guards/ClientGuard';
import PageDashboard from 'src/pages/PageDashboard';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: <PageLogin />,
        },
      ],
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <ClientGuard>
            <DashboardLayout />
          </ClientGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard" replace />, index: true },
        { path: 'dashboard', element: <PageDashboard /> },
        {
          path: 'instruments',
          children: [
            { element: <Navigate to="/instruments/list" replace />, index: true },
            { path: 'list', element: <PageInstruments /> },
            { path: 'list/:clientID', element: <PageInstruments /> },
            { path: 'list/:clientID/:locationID', element: <PageInstruments /> },
            { path: 'new', element: <PageAddEditViewInstrument /> },
            { path: ':instrumentID/view', element: <PageAddEditViewInstrument /> },
            { path: ':instrumentID/edit', element: <PageAddEditViewInstrument /> },
            { path: ':instrumentID/calibrate', element: <PageCalibrateInstrument /> },
            { path: ':instrumentID/check', element: <PageReferenceCheckInstrument /> },
          ],
        },
        {
          path: 'references',
          children: [
            { element: <Navigate to="/references/list" replace />, index: true },
            {
              path: 'list',
              element: <PageReferences />,
            },
            { path: 'new', element: <PageAddEditViewReference /> },
            { path: ':referenceID/view', element: <PageAddEditViewReference /> },
            { path: ':referenceID/edit', element: <PageAddEditViewReference /> },
          ],
        },
        {
          path: 'reports',
          children: [
            { element: <Navigate to="/reports/overview" replace />, index: true },
            { path: 'overview', element: <PageReports /> },
          ],
        },
        {
          path: 'database',
          children: [
            { element: <Navigate to="/database/units" replace />, index: true },
            { path: 'units', element: <PageUnits /> },
          ],
        },
        {
          path: 'clients',
          children: [
            { element: <Navigate to="clients/list" replace />, index: true },
            { path: 'list', element: <PageClients /> },
            { path: 'list/:clientID', element: <PageClients /> },
            { path: 'list/:clientID/:locationID', element: <PageClients /> },
          ],
        },

        {
          path: 'devices',
          children: [
            { element: <Navigate to="/devices/list" replace />, index: true },
            { path: 'list', element: <PageDevices /> },
            { path: 'new', element: <PageCreateEditViewDevice /> },
            { path: ':deviceID/view', element: <PageCreateEditViewDevice /> },
            { path: ':deviceID/edit', element: <PageCreateEditViewDevice /> },
          ],
        },

        {
          path: 'users',
          children: [
            { element: <Navigate to="/users/list" replace />, index: true },
            { path: 'list', element: <PageUsers /> },
            { path: ':userID/edit', element: <PageAddEditUser /> },
            { path: 'new', element: <PageAddEditUser /> },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// Dashboard
const PagePlaceholder = Loadable(lazy(() => import('../pages/PagePlaceholder')));
const PageLogin = Loadable(lazy(() => import('../pages/PageLogin')));

const PageUsers = Loadable(lazy(() => import('../pages/PageUsers')));
const PageAddEditUser = Loadable(lazy(() => import('../pages/PageAddEditUser')));

const PageInstruments = Loadable(lazy(() => import('../pages/PageInstruments')));
const PageAddEditViewInstrument = Loadable(lazy(() => import('../pages/PageAddEditViewInstrument')));
const PageCalibrateInstrument = Loadable(lazy(() => import('../pages/PageCalibrateInstrument')));
const PageReferenceCheckInstrument = Loadable(lazy(() => import('../pages/PageReferenceCheckInstrument')));

const PageReferences = Loadable(lazy(() => import('../pages/PageReferences')));
const PageAddEditViewReference = Loadable(lazy(() => import('../pages/PageAddEditViewReference')));

const PageReports = Loadable(lazy(() => import('../pages/PageReports')));

const PageDevices = Loadable(lazy(() => import('../pages/PageDevices')));
const PageCreateEditViewDevice = Loadable(lazy(() => import('../pages/PageCreateEditViewDevice')));

const PageClients = Loadable(lazy(() => import('../pages/PageClients')));
const PageUnits = Loadable(lazy(() => import('../pages/PageUnits')));

const NotFound = Loadable(lazy(() => import('../pages/Page404')));
